import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { API } from "@/lib/brand";
import { JOURNAL_CATEGORIES } from "@/lib/journal";
import PostCard from "@/components/journal/PostCard";
import JournalMasthead from "@/components/journal/JournalMasthead";
import { Loader2 } from "lucide-react";

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/journal/posts`);
        const data = res.ok ? await res.json() : [];
        setPosts(Array.isArray(data) ? data : []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return posts;
    return posts.filter((p) => p.category === filter);
  }, [posts, filter]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-16">
      <Helmet>
        <title>The Reliserve Wealth Journal</title>
        <meta
          name="description"
          content="Practical notes on wealth, protection and planning from Reliserve Wealth — goal-based investing, inflation, insurance and more."
        />
        <meta property="og:title" content="The Reliserve Wealth Journal" />
        <meta
          property="og:description"
          content="Practical notes on wealth, protection and planning from Reliserve Wealth."
        />
      </Helmet>

      <JournalMasthead />

      <section className="mt-6">
        <div className="flex flex-wrap items-center gap-2" data-testid="journal-filters">
          <FilterChip value="all" label="All posts" active={filter === "all"} onClick={setFilter} />
          {JOURNAL_CATEGORIES.map((c) => (
            <FilterChip
              key={c.value}
              value={c.value}
              label={c.label}
              active={filter === c.value}
              onClick={setFilter}
            />
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center text-ink-soft">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-ink-soft" data-testid="journal-empty">
            No posts yet. Check back soon.
          </div>
        ) : (
          <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="journal-grid">
            {filtered.map((p) => (
              <PostCard key={p.id} post={p} testId={`post-card-${p.slug}`} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function FilterChip({ value, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`px-4 py-1.5 rounded-full text-[12.5px] transition-colors border hairline ${
        active
          ? "bg-ink text-paper border-ink"
          : "text-ink-soft hover:text-ink hover:border-ink"
      }`}
      data-testid={`filter-${value}`}
    >
      {label}
    </button>
  );
}
